import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Loader2,
  MessageSquare,
  Search,
  ShieldAlert,
  Users,
} from "lucide-react";
import { useAronCosts } from "../api/aronCostsApi";
import {
  useAdminConversationOwners,
  useAdminConversations,
} from "../api/aronAdminApi";
import type { AronAdminConversation } from "../types";
import {
  AronAuditTranscript,
  PROVIDER_DOT,
  ProviderChip,
  fmtDateTime,
  fmtInt,
  fmtUsd,
} from "../components/AronAuditView";

function StatsStrip() {
  const { data } = useAronCosts();
  if (!data) return null;
  const { meta } = data;
  return (
    <div className="flex flex-wrap gap-2">
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800">
        <span className="text-xs text-gray-500">Łączny koszt </span>
        <span className="font-semibold text-red-600">
          {fmtUsd(meta.total_cost_usd)} $
        </span>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800">
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <span className={`h-2 w-2 rounded-full ${PROVIDER_DOT.claude}`} />{" "}
          Claude
        </span>{" "}
        <span className="font-medium">
          {fmtUsd(meta.total_cost_claude_usd)} $
        </span>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800">
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <span className={`h-2 w-2 rounded-full ${PROVIDER_DOT.deepseek}`} />{" "}
          DeepSeek
        </span>{" "}
        <span className="font-medium">
          {fmtUsd(meta.total_cost_deepseek_usd)} $
        </span>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800">
        <span className="text-xs text-gray-500">Tokeny </span>
        <span className="font-medium">{fmtInt(meta.total_tokens)}</span>
      </div>
    </div>
  );
}

function ConversationRow({
  conv,
  selected,
  onSelect,
}: {
  conv: AronAdminConversation;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-lg border p-2.5 text-left transition ${
        selected
          ? "border-red-300 bg-red-50/60 dark:border-red-800 dark:bg-red-900/20"
          : "border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {conv.title || "(bez tytułu)"}
            {conv.deleted_at && (
              <span className="ml-1 rounded bg-gray-200 px-1 text-[10px] text-gray-500 dark:bg-gray-700">
                usunięta
              </span>
            )}
          </div>
          <div className="truncate text-xs text-gray-400">{conv.user_name}</div>
        </div>
        <span className="shrink-0 text-xs font-medium text-red-600">
          {fmtUsd(conv.total_cost_usd)} $
        </span>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <ProviderChip provider={conv.provider} model={conv.model_id} />
        <span className="flex items-center gap-1 text-[11px] text-gray-400">
          <MessageSquare size={11} /> {conv.message_count} ·{" "}
          {fmtDateTime(conv.last_activity_at ?? conv.last_message_at)}
        </span>
      </div>
    </button>
  );
}

type SortField = "activity" | "cost" | "user";
const SORT_LABEL: Record<SortField, string> = {
  activity: "Data",
  cost: "Koszt",
  user: "Pracownik",
};

function SortButton({
  field,
  active,
  dir,
  onClick,
}: {
  field: SortField;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs transition ${
        active
          ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
          : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
      }`}
    >
      {SORT_LABEL[field]}
      {active &&
        (dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
    </button>
  );
}

export function AronAuditPage() {
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [sortField, setSortField] = useState<SortField>("activity");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<string | null>(null);
  // Every filter and the sort go to the SERVER: narrowing the page already on screen
  // would hide colleagues whose threads sit further down the list.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAdminConversations({
      search: search.trim() || undefined,
      provider: provider || undefined,
      user: userFilter || undefined,
      ordering: sortField,
      dir: sortDir,
    });
  const { data: ownerRows } = useAdminConversationOwners();

  const displayed = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.rows),
    [data],
  );
  const total = data?.pages[0]?.count ?? 0;
  // The dropdown lists everyone who has ever used ARON, not just the loaded page.
  const owners = ownerRows ?? [];

  function toggleSort(f: SortField) {
    if (sortField === f) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(f);
      setSortDir(f === "user" ? "asc" : "desc");
    }
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-red-600 via-red-700 to-black text-white">
          <Eye size={16} />
        </span>
        <div>
          <h1 className="text-lg font-semibold">Audyt ARON</h1>
          <p className="text-xs text-gray-500">Pełny wgląd w użycie ARON</p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
        <ShieldAlert size={15} className="mt-0.5 shrink-0" />
        Rozmowy mogą zawierać dane wrażliwe (płace, finanse, PII).
      </div>

      <StatsStrip />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2.5 top-2.5 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj: tytuł, pracownik, e-mail…"
            className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-red-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800"
          />
        </div>
        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-red-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800"
        >
          <option value="">Wszyscy dostawcy</option>
          <option value="claude">Claude</option>
          <option value="deepseek">DeepSeek</option>
        </select>
        <div className="relative">
          <Users
            size={14}
            className="pointer-events-none absolute left-2.5 top-2.5 text-gray-400"
          />
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-2 text-sm focus:border-red-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Wszyscy pracownicy</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.conversation_count})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-400">Sortuj:</span>
        {(["activity", "cost", "user"] as const).map((f) => (
          <SortButton
            key={f}
            field={f}
            active={sortField === f}
            dir={sortDir}
            onClick={() => toggleSort(f)}
          />
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        {/* List */}
        <div
          className={`space-y-2 lg:col-span-1 ${selected ? "hidden lg:block" : ""}`}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Wczytuję rozmowy…
            </div>
          ) : displayed.length > 0 ? (
            <>
              <p className="px-1 text-xs text-gray-400">
                {displayed.length}
                {displayed.length !== total ? ` z ${total}` : ""} rozmów
              </p>
              {displayed.map((c) => (
                <ConversationRow
                  key={c.id}
                  conv={c}
                  selected={selected === c.id}
                  onSelect={() => setSelected(c.id)}
                />
              ))}
              {hasNextPage && (
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="w-full rounded-lg border border-gray-200 py-2 text-xs text-gray-500 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                  {isFetchingNextPage ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 size={12} className="animate-spin" /> Wczytuję…
                    </span>
                  ) : (
                    `Pokaż więcej (${total - displayed.length})`
                  )}
                </button>
              )}
            </>
          ) : (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400 dark:border-gray-700">
              Brak rozmów.
            </p>
          )}
        </div>

        {/* Transcript */}
        <div
          className={`rounded-xl border border-gray-200 dark:border-gray-700 lg:col-span-2 lg:min-h-[60vh] ${
            selected ? "" : "hidden lg:flex"
          }`}
        >
          {selected ? (
            <AronAuditTranscript id={selected} onBack={() => setSelected(null)} />
          ) : (
            <div className="flex flex-1 items-center justify-center p-10 text-center text-sm text-gray-400">
              <span className="flex flex-col items-center gap-2">
                <MessageSquare size={22} className="text-gray-300" />
                Wybierz rozmowę z listy, aby zobaczyć transkrypt.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
